import { useState } from "react";
import logo from "@/assets/image/psp-logo.svg";
import style from "./style.module.css";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { Form, Input, notification } from "antd";
import { UserInviteConfirmationHttpService } from "./services/user-invite-confirmation.service";
import { useGetQueryCode } from "@/shared/hooks/use-get-query-code.hook";
import { getFriendlyErrorMessage } from "@/shared/services/http";

const sanitizeEmail = (email: string) => {
  return email.trim().toLowerCase();
};

export function AcceptInviteConfirmation() {
  const sourceEmail = String(useGetQueryCode("email"));
  const [submissionState, setSubmissionState] = useState<"idle" | "pending" | "success" | "error">("idle");

  const handleSubmit = async (values: { email: string }) => {
    try {
      setSubmissionState("pending");
      const sanitizedEmail = sanitizeEmail(values.email);
      // Little bit of obfuscation to prevent directly exposing the source email to the user
      if (sanitizeEmail(sourceEmail) === sanitizedEmail) {
        await UserInviteConfirmationHttpService.acceptUserInvite(sanitizedEmail);
      }
      setSubmissionState("success");
      notification.success({
        message:
          "Invite Accepted! If your account exists, you will receive an email with further instructions on setting up your account.",
      });
    } catch (error) {
      setSubmissionState("error");
      notification.error({
        message:
          getFriendlyErrorMessage(error) ?? "Oops, something went wrong. Please try again or contact support for help.",
      });
    }
  };

  const isSubmitting = submissionState === "pending";
  const isSuccess = submissionState === "success";

  return (
    <div className={style.wrapper}>
      <div className={style.logo}>
        <img src={logo} className={style.logoImage} alt="PSP Metrics" />
      </div>
      <div className={style.container}>
        <h1 className={style.title}>Accept Your Invitation</h1>
        {isSuccess ? (
          <p className={style.text}>Thank you! Please check your email for further instructions.</p>
        ) : (
          <>
            <p className={style.text}>
              If you have an account, you'll receive an email requesting you to create a new password for your account.
            </p>
            <p className={style.text}>
              Once requested, you'll have about an hour to complete the process before the link becomes invalid.
            </p>
            <Form layout="vertical" onFinish={handleSubmit} requiredMark={false} initialValues={{ email: "" }}>
              <Form.Item
                name="email"
                className={style.baseInput}
                label="Confirm your email address"
                rules={[
                  {
                    type: "email",
                    required: true,
                    message: "Please enter a valid email address",
                  },
                ]}
                validateTrigger={["onBlur"]}
              >
                <Input />
              </Form.Item>
              <div className={style.buttonWrapper}>
                <SubmitButton isLoading={isSubmitting} disabled={isSubmitting}>
                  Accept Invite
                </SubmitButton>
              </div>
            </Form>
          </>
        )}
      </div>
    </div>
  );
}
